import { FC, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SimeraDark } from '$icons/simera-dark logo.svg';
import { ReactComponent as Add } from '$icons/uncolored/Add.svg';
import AccountButton from '../AccountButton/AccountButton';
import DropdownMenu from '../DropdownMenu/DropdownMenu';
import AddNewJobModal from '../AddNewJobModal/AddNewJobModal';
import EmployerTermOfServiceModal from './EmployerTermOfService/EmployerTermOfServiceModal';

export enum ActiveEmployerLayoutPageType {
  positions,
  teamMembers,
}

interface HomeLayoutProps {
  children?: React.ReactNode;
  externalIsModalPositionOpen?: boolean | undefined;
  activePage: ActiveEmployerLayoutPageType;
}

const EmployerSPKLayout: FC<HomeLayoutProps> = ({
  children,
  externalIsModalPositionOpen,
  activePage,
}) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [openTermOfService, setOpenTermOfService] = useState(false);
  const { t } = useTranslation('global');
  const [isModalPositionOpen, setIsModalPositionOpen] = useState(false);

  const toggleIsModalPositionOpen = useCallback(
    () => setIsModalPositionOpen((prev) => !prev),
    []
  );

  useEffect(() => {
    if (externalIsModalPositionOpen) {
      setIsModalPositionOpen(true);
    }
  }, [externalIsModalPositionOpen]);

  const serverEmployer = JSON.parse(
    localStorage.getItem('ServerEmployerResponse') || '{}'
  );

  useEffect(() => {
    if (serverEmployer && serverEmployer.AgreedTermsOfServiceDate === null) {
      setOpenTermOfService(true);
    }
  }, [serverEmployer]);

  return (
    <div
      data-testid="employer-container"
      className="font-mulish min-h-screen bg-spk-offWhite"
    >
      <nav className="flex flex-row bg-spk-neutral-10 py-2">
        <div className="container m-auto flex flex-row items-center px-2 md:p-0">
          <Link to="/employer-dashboard/positions">
            <SimeraDark className="h-6 w-auto" />
          </Link>
          <div className="ml-auto flex flex-row gap-8 items-center">
            <Link
              to="/employer-dashboard/team-members"
              className={`${
                activePage === ActiveEmployerLayoutPageType.teamMembers
                  ? 'underline'
                  : 'text-spk-brightBlue'
              } font-bold hidden md:flex`}
            >
              {t('EmployerDashboard.TeamMembers')}
            </Link>
            <Link
              className={`${
                activePage === ActiveEmployerLayoutPageType.positions
                  ? 'underline'
                  : 'text-spk-brightBlue'
              } font-bold hidden md:flex`}
              to="/employer-dashboard/positions"
            >
              {t('EmployerDashboard.Positions')}
            </Link>
            <button
              type="button"
              onClick={toggleIsModalPositionOpen}
              className="text-spk-brightBlue flex-row items-center rounded-full gap-4 bg-spk-softBlue px-6 py-3 font-bold hidden md:flex"
            >
              <Add className="fill-spk-brightBlue" />
              {t('EmployerDashboard.AddPosition')}
            </button>
            <div className="relative">
              <AccountButton
                isProfile
                t={t}
                action={() => setOpenMenu(!openMenu)}
                hideName
              />
              {openMenu && (
                <DropdownMenu
                  isEmployer
                  setModal={setOpenMenu}
                  onAddPosition={toggleIsModalPositionOpen}
                  className="!w-40 top-2 right-0"
                />
              )}
            </div>
          </div>
        </div>
      </nav>
      {children && <div className="container m-auto">{children}</div>}
      <AddNewJobModal
        onClose={toggleIsModalPositionOpen}
        modalIsOpen={isModalPositionOpen}
      />
      <EmployerTermOfServiceModal
        modalIsOpen={openTermOfService}
        employerId={serverEmployer?.Id}
        setOpenTermOfService={setOpenTermOfService}
      />
    </div>
  );
};

EmployerSPKLayout.defaultProps = {
  children: null,
  externalIsModalPositionOpen: undefined,
};

export default EmployerSPKLayout;
